import type { ComponentProps } from 'react';
import { useCallback, useMemo } from 'react';

import type { MPLPreApprovalOffer } from 'commonTypes/api/scenarios/CreditSelectionScreenContent';

import { isMplPreApprovalOffer } from 'shared/lib';
import {
  sendEventOnCorrectSmsCodeForPreApproval,
  sendEventOnFailedMplPreApprovalSmsConfirmation,
  sendEventOnRequestNewSmsCodeForPreApproval,
  sendEventOnShowMplPreApprovalSmsConfirmation,
  sendEventPreApprovedSmsModalClick,
  sendEventPreApprovedSmsModalShow,
} from 'shared/lib/sendGAEvents';
import type { TOffer } from 'shared/types/TRankingService';

import { getAnalyticsProductParams } from 'entities/analytics';

import type { ConfirmationStep } from '../ui/ConfirmationStep';

import { useCommonMplAnalyticsParams } from './useCommonMplAnalyticsParams';

type ConfirmationFormEvents = Pick<ComponentProps<typeof ConfirmationStep>, 'onSmsCodeEvent' | 'onShowEvent'>;

export const useConfirmationStepAnalyticsEvents = (offer: TOffer | MPLPreApprovalOffer): ConfirmationFormEvents => {
  const commonMplAnalyticsParams = useCommonMplAnalyticsParams();

  const onSmsCodeEvent: NonNullable<ConfirmationFormEvents['onSmsCodeEvent']> = useCallback(
    (offerStepData, type) => {
      const { bankName, preApprovalStatus, productName, productId, bankId } = offerStepData || {};
      const { offerType } = getAnalyticsProductParams(offer);

      switch (type) {
        case 'verified': {
          if (isMplPreApprovalOffer(offer)) {
            sendEventOnCorrectSmsCodeForPreApproval({
              ...commonMplAnalyticsParams,
              organizationName: bankName,
              productId,
              organizationId: bankId,
              offerType,
            });
            return;
          }
          sendEventPreApprovedSmsModalClick({
            bankName,
            offerName: productName,
            offerStatus: preApprovalStatus || '',
            action: 'Ввод смс',
          });
          break;
        }
        case 'failed': {
          if (isMplPreApprovalOffer(offer)) {
            sendEventOnFailedMplPreApprovalSmsConfirmation({
              ...commonMplAnalyticsParams,
              organizationName: bankName,
              productId,
              organizationId: bankId,
              offerType,
            });
            return;
          }
          sendEventPreApprovedSmsModalClick({
            bankName,
            offerName: productName,
            offerStatus: preApprovalStatus || '',
            action: 'Ошибка кода',
          });
          break;
        }
        case 'tooManyRequests': {
          sendEventPreApprovedSmsModalClick({
            bankName,
            offerName: productName,
            offerStatus: preApprovalStatus || '',
            action: 'Конец попыток',
          });
          break;
        }
        case 'requestNewCode': {
          if (isMplPreApprovalOffer(offer)) {
            sendEventOnRequestNewSmsCodeForPreApproval({
              ...commonMplAnalyticsParams,
              organizationName: bankName,
              productId,
              organizationId: bankId,
              offerType,
            });
          }
          break;
        }
        default: {
          /* empty */
        }
      }
    },
    [commonMplAnalyticsParams, offer],
  );

  const onShowEvent: NonNullable<ConfirmationFormEvents['onShowEvent']> = useCallback(
    (offerStepData) => {
      const { bankName, preApprovalStatus, productName, productId, bankId } = offerStepData || {};
      const { offerType } = getAnalyticsProductParams(offer);

      if (isMplPreApprovalOffer(offer)) {
        sendEventOnShowMplPreApprovalSmsConfirmation({
          ...commonMplAnalyticsParams,
          organizationName: bankName,
          productId,
          organizationId: bankId,
          offerType,
        });
        return;
      }

      sendEventPreApprovedSmsModalShow({
        bankName,
        offerName: productName,
        offerStatus: preApprovalStatus || '',
      });
    },
    [commonMplAnalyticsParams, offer],
  );

  return useMemo(
    () => ({
      onSmsCodeEvent,
      onShowEvent,
    }),
    [onSmsCodeEvent, onShowEvent],
  );
};
